import moment from "jalali-moment";
import {
  LegacyRef,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactPaginate from "react-paginate";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import endpoints from "../../config/endpoints";
import usePagination from "../../hooks/usePagination";
import { useResponsive } from "../../hooks/useResponsive";
import { popupSlice } from "../../services/reducers";
import { setLoading } from "../../services/reducers/homeSlice";
import restApi from "../../services/restApi";
import tools from "../../utils/tools";
import { Sidebar } from "../../layouts/Sidebar";
import Message from "../../layouts/Modal/Message";
import { IPortfolio } from "types/types";
import { useAppSelector } from "services/store";

type key = "isOnStory" | "isOnPortfolio" | "delete";

const Portfolio = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<any>([]);
  const [query, setQuery] = useState(searchParams.get("query") || "");
  const [tab, setTab] = useState(searchParams.get("tab") || "all");
  const [form, setForm] = useState<
    Record<
      string,
      { isOnPortfolio?: boolean; isOnStory?: boolean; delete?: boolean }
    >
  >({});
  const {
    pageCount,
    endOffset,
    itemOffset,
    itemsPerPage,
    setPageCount,
    setItemOffset,
    setItemsPerPage,
  } = usePagination(data);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const allServices = useAppSelector(
    (state) => state.serviceReducer.allServices,
  );
  const { isMobile, isTablet } = useResponsive();
  const tabTitles = {
    all: "همه",
    NotChecked: "بررسی نشده",
    OnStory: "استوری فعلی",
    BeenOnStory: "آرشیو استوری",
    OnPortfolio: "در پورتفولیو",
  };

  const updateFormField = (id: number, key: key, value: boolean) => {
    setForm((prev) => {
      const cp = { ...prev };

      if (key == "delete" && value) {
        cp[id.toString()] = {
          delete: true,
        };
        return cp;
      }

      if (value) {
        if (!cp[id.toString()] || cp[id.toString()]?.delete) {
          cp[id.toString()] = {};
        }

        cp[id.toString()][key] = value;
      } else {
        delete cp[id.toString()][key];
      }
      return cp;
    });
  };
  const submit = async () => {
    const result = await Swal.fire({
      title: "آیا مطمئن هستید؟",
      text: "این عمل قابل بازگشت نیست",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#c89dc7",
      confirmButtonText: "بله",
      cancelButtonText: "انصراف",
    });

    if (result.isConfirmed) {
      dispatch(setLoading(true));

      try {
        const res = await restApi(endpoints.portfolio.index, true).post(form);

        if (res.code == 200) {
          Swal.fire({
            title: "موفق",
            text: "پورتفولیو با موفقیت بروزرسانی شد",
            icon: "success",
            confirmButtonText: "متوجه شدم",
          });
          setForm({});
          fetchPortfolios(); // Refresh data
        } else {
          Swal.fire({
            title: "ناموفق",
            text: res?.data || "خطا در بروزرسانی پورتفولیو",
            icon: "error",
            confirmButtonText: "متوجه شدم",
          });
        }
      } catch (error) {
        Swal.fire({
          title: "خطا",
          text: "خطا در اتصال به سرور",
          icon: "error",
          confirmButtonText: "متوجه شدم",
        });
      }

      dispatch(setLoading(false));
    }
  };

  const list = () => {
    const rows: ReactElement[] = [];

    data?.portfolios?.map((portfolio: IPortfolio, index: number) => {
      rows.push(
        <article key={portfolio.orderId} className="portfolioCard">
          <img src={portfolio.mediaUrl} />
          <div className="portfolioCardLeft">
            <span>
              <a href={`/Order/edit/${portfolio.order.id}`} target="_blank">
                {portfolio.order.code}
              </a>
            </span>
            <span>
              <a
                href={`/User/edit/${portfolio.worker.id}`}
                target="_blank"
              >{`${portfolio.worker.name} ${portfolio.worker.lastName}`}</a>
            </span>
          </div>
          <div className="portfolioCardRight">
            {portfolio.services.map((service) => {
              const attrFirstAncestor = tools
                .findAncestors(allServices, service.id)
                .find((e) => e?.showInList && e?.openDrawer);
              return attrFirstAncestor ? (
                <img
                  key={service.slug + index}
                  title={service.slug}
                  src={`/img/services/${attrFirstAncestor?.slug}.png`}
                />
              ) : (
                <span>{service.title}</span>
              );
            })}
          </div>
          <div className="portfolioCardButtons">
            <button
              className={
                "portfolioCardButton" +
                (form[portfolio.id] && form[portfolio.id]?.isOnStory
                  ? " storyActive"
                  : "")
              }
              onClick={() =>
                updateFormField(
                  portfolio.id,
                  "isOnStory",
                  !form[portfolio.id]?.isOnStory,
                )
              }
            >
              استوری
            </button>
            <button
              className={
                "portfolioCardButton" +
                (form[portfolio.id] && form[portfolio.id]?.isOnPortfolio
                  ? " portfolioActive"
                  : "")
              }
              onClick={() =>
                updateFormField(
                  portfolio.id,
                  "isOnPortfolio",
                  !form[portfolio.id]?.isOnPortfolio,
                )
              }
            >
              پورتفولیو
            </button>
            <button
              className={
                "portfolioCardButton" +
                (form[portfolio.id]?.delete ? " deleteActive" : "")
              }
              onClick={() =>
                updateFormField(
                  portfolio.id,
                  "delete",
                  !form[portfolio.id]?.delete,
                )
              }
            >
              هیچکدام
            </button>
          </div>
        </article>,
      );
    });

    return rows;
  };

  const fetchPortfolios = async () => {
    !query && dispatch(setLoading(true));

    const params: any = {
      page: searchParams.get("page") || 1,
      perPage: itemsPerPage,
      query: query,
    };

    if (tab != "all") {
      params.status = tab;
    }

    try {
      const res = await restApi(endpoints.portfolio.index, true).get(params);
      setData({
        ...res.data,
        statusCount: {
          ...res.data.statusCount,
          all: {
            title: "همه",
            count: Object.values(res.data.statusCount).reduce(
              (acc, curr) => acc + ((curr as any).count || 0),
              0,
            ),
          },
        },
      });
    } catch (error) {
      console.error("Error fetching portfolios:", error);
      Swal.fire({
        title: "خطا",
        text: "خطا در دریافت اطلاعات",
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
    } finally {
      !query && dispatch(setLoading(false));
    }
  };
  useEffect(() => {
    fetchPortfolios();
  }, [searchParams.get("page"), itemsPerPage, query, tab]);

  useEffect(() => {
    if (data.count) {
      const newPage =
        Number(searchParams.get("page")) <= Math.ceil(data.count / itemsPerPage)
          ? searchParams.get("page") || "1"
          : "1";
      setSearchParams({
        ["page"]: newPage,
        ["tab"]: tab,
        query: query,
      });
    }
  }, [data]);

  return (
    <>
      <div className="dashboardBody">
        <Sidebar />
        <main className="dashBoardMain">
          <h1 className="dashBoardTitle">پورتفولیو</h1>
          <div className="dashTabs">
            {Object.entries(tabTitles).map(([key, value]) => (
              <span
                key={key}
                className={`ordersTag clickable ${key == tab ? "activeTab" : ""}`}
                onClick={() => {
                  setTab(key);
                  setSearchParams({ ["page"]: "1", ["tab"]: key });
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setTab(key);
                    setSearchParams({ ["page"]: "1", ["tab"]: key });
                  }
                }}
              >
                {value}
                {data?.statusCount && (
                  <span
                    className={`numberTag ${key == tab ? "activeTab" : ""}`}
                  >
                    {data?.statusCount[key]?.count || 0}
                  </span>
                )}
              </span>
            ))}
          </div>

          <div className="searchContainer">
            <span className="backSpan keepRight">
              <span
                className="dashboardHeader clickable"
                onClick={submit}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && submit()}
              >
                <p>ثبت تغییرات</p>
                <i className="plusIcon"></i>
              </span>
            </span>
            <div className="itemsPerPage">
              <label htmlFor="itemPerPage">تعداد در صفحه</label>
              <input
                id="itemPerPage"
                type="number"
                min="1"
                max="100"
                defaultValue={itemsPerPage}
                onChange={(input) => {
                  const value = Number(input.target.value);
                  if (value && value > 0 && value <= 100) {
                    setItemsPerPage(value);
                  }
                }}
                onBlur={(e) => {
                  if (!e.target.value || Number(e.target.value) <= 0) {
                    e.target.value = itemsPerPage.toString();
                  }
                }}
              />
            </div>
            <div className="dashboardseaechBox">
              <i className="dashMagnifierIcon"></i>
              <input
                className="dashSearchInput"
                placeholder="جستجو"
                value={query}
                onChange={(input) => setQuery(input.target.value)}
                aria-label="جستجوی سفارشات"
              />
            </div>
          </div>
          <ReactPaginate
            breakLabel="..."
            nextLabel="بعدی >"
            onPageChange={(event) => {
              setSearchParams({
                ["page"]: (Number(event.selected) + 1).toString(),
                ["tab"]: tab,
              });
              setItemOffset(
                (event.selected * itemsPerPage) %
                  (data?.portfolios?.length || 1),
              );
            }}
            forcePage={Number(searchParams.get("page")) - 1}
            initialPage={
              searchParams.get("page") != null
                ? Number(searchParams.get("page")) - 1
                : 0
            }
            pageRangeDisplayed={isMobile ? 3 : 5}
            pageCount={pageCount}
            previousLabel="< قبلی"
            renderOnZeroPageCount={null}
            className="pagination"
            pageClassName="paginationBreak"
            previousClassName="paginationBreak"
            nextClassName="paginationBreak"
            activeClassName="paginationActive"
            disabledClassName="paginationDisabled"
          />
          <div className="portfolioContainer">{list()}</div>

          <ReactPaginate
            breakLabel="..."
            nextLabel="بعدی >"
            onPageChange={(event) => {
              setSearchParams({
                ["page"]: (Number(event.selected) + 1).toString(),
                ["tab"]: tab,
              });
              setItemOffset(
                (event.selected * itemsPerPage) %
                  (data?.portfolios?.length || 1),
              );
            }}
            forcePage={Number(searchParams.get("page")) - 1}
            initialPage={
              searchParams.get("page") != null
                ? Number(searchParams.get("page")) - 1
                : 0
            }
            pageRangeDisplayed={isMobile ? 3 : 5}
            pageCount={pageCount}
            previousLabel="< قبلی"
            renderOnZeroPageCount={null}
            className="pagination"
            pageClassName="paginationBreak"
            previousClassName="paginationBreak"
            nextClassName="paginationBreak"
            activeClassName="paginationActive"
            disabledClassName="paginationDisabled"
          />
        </main>
      </div>
    </>
  );
};

export default Portfolio;
